import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useEnsureTemplateDefaultsMutation } from "../api/documentTemplateApi";

/** One ensure-defaults POST per org per page load, shared across all hook instances. */
const ensuredOrgIds = new Set();
const inflightByOrgId = new Map();

function ensureDefaultsForOrg(orgId, ensureTemplateDefaults) {
  if (ensuredOrgIds.has(orgId)) {
    return Promise.resolve();
  }

  const existing = inflightByOrgId.get(orgId);
  if (existing) return existing;

  const promise = ensureTemplateDefaults()
    .unwrap()
    .then(() => {
      ensuredOrgIds.add(orgId);
    })
    .finally(() => {
      inflightByOrgId.delete(orgId);
    });

  inflightByOrgId.set(orgId, promise);
  return promise;
}

/**
 * Idempotent seed for Default Invoice / Proposal / Upsell — once per org per session.
 * @param {{ onSeeded?: () => void }} [options]
 */
export function useEnsureTemplateDefaultsOnMount({ onSeeded } = {}) {
  // `state.organisation.currentOrg` only gets populated after visiting
  // Settings -> Organisation Details (useOrganisationDetailsForm's
  // refetchOrg is the only place that ever dispatches setCurrOrg) - for
  // every other screen it stays null for the whole session, so this hook
  // silently never fired. `state.auth.user.selectedOrg` (same id the
  // backend reads off the JWT as `decodedToken.selectedOrg`) is always
  // populated once logged in - matches PrivateRoute's own selectedOrg read.
  const orgId = useSelector((state) => state.auth.user?.selectedOrg) || "";
  const [ensureTemplateDefaults] = useEnsureTemplateDefaultsMutation();
  const onSeededRef = useRef(onSeeded);

  useEffect(() => {
    onSeededRef.current = onSeeded;
  }, [onSeeded]);

  useEffect(() => {
    if (!orgId) return undefined;

    let cancelled = false;

    ensureDefaultsForOrg(orgId, ensureTemplateDefaults)
      .then(() => {
        if (!cancelled) onSeededRef.current?.();
      })
      .catch(() => {
        ensuredOrgIds.delete(orgId);
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, ensureTemplateDefaults]);
}
